import type { HttpActivity } from './types';

/**
 * What `RemoteModule` reports for each request a widget makes through its `httpClient`
 * prop, from `@invent/platform-api` 1.25.0 on. The shape is platform-api's
 * `WidgetHttpRequestSettled`, mirrored here until the shell depends on that version.
 */
export interface WidgetHttpRequestSettled {
  scope: string;
  instanceId?: string;
  method: string;
  /** As the widget passed it: placeholders unfilled, query params not yet added. */
  url: string;
  status?: number;
  durationMs: number;
  error?: string;
  /** The `traceId` the caller put in its options. */
  label?: string;
}

/** The slot platform-api reads on the gateway: `window.sharedHostGateway.activity`. */
export interface WidgetActivitySink {
  httpRequestSettled(settled: WidgetHttpRequestSettled): void;
}

const MAX_LABEL_LENGTH = 64;

/** A url without its query string and fragment, relative or not. */
export function withoutQuery(url: string): string {
  return url.split(/[?#]/, 1)[0] ?? url;
}

export function toHttpActivity(settled: WidgetHttpRequestSettled, time: number): HttpActivity {
  const entry: HttpActivity = {
    kind: 'http',
    time,
    scope: settled.scope,
    method: settled.method.toUpperCase(),
    url: withoutQuery(settled.url),
    durationMs: Math.max(0, Math.round(settled.durationMs))
  };
  if (settled.instanceId !== undefined) {
    entry.instanceId = settled.instanceId;
  }
  if (settled.status !== undefined) {
    entry.status = settled.status;
  }
  if (settled.error !== undefined) {
    entry.error = settled.error;
  }
  if (settled.label !== undefined) {
    entry.label = settled.label.slice(0, MAX_LABEL_LENGTH);
  }
  return entry;
}

export function createWidgetActivitySink(
  record: (entry: HttpActivity) => void,
  now: () => number = Date.now
): WidgetActivitySink {
  return {
    httpRequestSettled(settled) {
      record(toHttpActivity(settled, now()));
    }
  };
}
